import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { io as ioClient } from "socket.io-client";
import api, { API_ORIGIN, assetUrl } from "../api";
import { filterFiles, isAttachmentFile, MAX_ATTACHMENTS, uploadErrorMessage } from "../uploadUtils";
import {
  Calendar, Clock, FileText, Download, Paperclip, Send, Check, X, Pencil,
  ChevronDown, ChevronLeft, Info, Image as ImageIcon, FileAudio, FileVideo, File as GenericFile, ExternalLink, Maximize2,
} from "lucide-react";
import { TabBar, TabPanel, buildRows } from "../components/QuotePanels";
import UserLayout from "../components/Userlayout";
import SafeImage from "../components/SafeImage";
import "./QuoteDetailsMobile.css";
import {
  money, fmtDate, fmtDateTime, pickCustomerQuote, customerQuoteState, quoteExpiry,
  customerVisibleVersions, numberVersions,
} from "../quoteUtils";

const initials = (name) => (name?.trim()?.[0] || "?").toUpperCase();
const fmtClock = (d) => (d ? new Date(d).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase() : "");

const duration = (from, to) => {
  if (!from || !to) return "—";
  const mins = Math.max(0, Math.round((new Date(to) - new Date(from)) / 60000));
  const d = Math.floor(mins / 1440), h = Math.floor((mins % 1440) / 60), m = mins % 60;
  return [d ? `${d}d` : "", h ? `${h}h` : "", `${m}m`].filter(Boolean).join(" ");
};

// Status pill shown beside the title (matches the design: "PENDING ●")
const PILL = {
  awaiting: { label: "Awaiting quote", cls: "bg-[#fff1dc] text-[#d9680a]", dot: "bg-[#f59a1f]" },
  sent: { label: "Pending", cls: "bg-[#fff1dc] text-[#d9680a]", dot: "bg-[#f59a1f]" },
  updating: { label: "Updating", cls: "bg-slate-100 text-slate-600", dot: "bg-slate-400" },
  revision_requested: { label: "Revision requested", cls: "bg-purple-50 text-purple-700", dot: "bg-purple-500" },
  approved: { label: "Approved", cls: "bg-green-50 text-green-700", dot: "bg-green-500" },
  declined: { label: "Declined", cls: "bg-red-50 text-red-600", dot: "bg-red-500" },
};

export default function QuoteDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [tab, setTab] = useState("overview");

  const [req, setReq] = useState(null);
  const [quote, setQuote] = useState(null);
  const [versionCount, setVersionCount] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [showOlder, setShowOlder] = useState(false);
  const [acting, setActing] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(Boolean(location.state?.revise)); // opened from "Revise Quote" in the list
  const [reviseText, setReviseText] = useState("");
  const [notice, setNotice] = useState("");
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const mobileChatRef = useRef(null);
  const desktopChatRef = useRef(null);

  // request + the version of the quote this customer is allowed to see
  const loadQuote = useCallback(async () => {
    try {
      const [r, q] = await Promise.all([
        api.get(`/requests/${id}`),
        api.get(`/requests/${id}/quotations`).catch(() => ({ data: [] })),
      ]);
      const quotations = Array.isArray(q.data) ? q.data : [];
      setReq(r.data);
      const visible = pickCustomerQuote(r.data, quotations);
      const shared = numberVersions(customerVisibleVersions(r.data, quotations), { useStored: false });
      setQuote(visible ? { ...visible, _v: shared.find((x) => x._id === visible._id)?._v || 1 } : null);
      setVersionCount(shared.length);
    } catch (e) {
      setLoadError(e.response?.status === 404 ? "This request could not be found." : "Could not load this quote.");
    }
  }, [id]);

  useEffect(() => {
    setReq(null);
    setLoadError("");
    loadQuote();
    api.get(`/requests/${id}/comments`).then((c) => setComments(c.data)).catch(() => setComments([]));
  }, [id, loadQuote]);

  useEffect(() => {
    for (const el of [mobileChatRef.current, desktopChatRef.current]) {
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [comments, showOlder]);

  // ---------- live: comments + quote sent/updated by the supplier ----------
  useEffect(() => {
    const socket = ioClient(API_ORIGIN, { transports: ["websocket", "polling"], auth: { token: localStorage.getItem("token") } });
    socket.emit("join", `request:${id}`);
    if (user?.id) socket.emit("join", `user:${user.id}`);
    socket.on("comment:new", (comment) => {
      if (String(comment.requestId) !== String(id)) return;
      setComments((prev) => (prev.some((m) => m._id === comment._id) ? prev : [...prev, comment]));
    });
    const refresh = (payload) => {
      const rid = payload?.requestDocId || payload?.requestId || payload?._id;
      if (!rid || String(rid) === String(id)) loadQuote();
    };
    socket.on("quote:update", refresh);
    socket.on("quotes:update", refresh);
    socket.on("request:update", refresh);
    return () => {
      socket.emit("leave", `request:${id}`);
      socket.disconnect();
    };
  }, [id, user?.id, loadQuote]);

  const addFiles = (list) => {
    const { accepted, problems } = filterFiles(list, { room: MAX_ATTACHMENTS - files.length, accept: isAttachmentFile });
    if (problems.length) alert(problems.join("\n"));
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
  };

  const send = async () => {
    if (sending || (!text.trim() && files.length === 0)) return;
    setSending(true);
    const fd = new FormData();
    fd.append("text", text);
    files.forEach((f) => fd.append("attachments", f));
    try {
      const { data } = await api.post(`/requests/${id}/comments`, fd);
      if (data?._id) setComments((prev) => (prev.some((m) => m._id === data._id) ? prev : [...prev, data]));
      setText("");
      setFiles([]);
    } catch (err) {
      alert(uploadErrorMessage(err, "Could not send your message."));
    } finally {
      setSending(false);
    }
  };

  const respond = async (status) => {
    if (!quote) return;
    const verb = status === "approved" ? "approve" : "decline";
    if (!window.confirm(`Are you sure you want to ${verb} quote ${quote.quoteId} for ${money(quote.pricing?.total)}?`)) return;
    setActing(true);
    setNotice("");
    try {
      await api.patch(`/quotations/${quote._id}/status`, { status });
      setNotice(status === "approved" ? "Quote approved. We'll be in touch about delivery." : "Quote declined.");
      await loadQuote();
    } catch (e) {
      alert(e.response?.data?.message || `Could not ${verb} the quote.`);
    } finally {
      setActing(false);
    }
  };

  const requestRevision = async () => {
    setActing(true);
    setNotice("");
    try {
      if (reviseText.trim()) {
        const fd = new FormData();
        fd.append("text", `Revision request: ${reviseText.trim()}`);
        await api.post(`/requests/${id}/comments`, fd);
      }
      await api.patch(`/requests/${id}/revise`);
      setReviseOpen(false);
      setReviseText("");
      setNotice("Revision requested. The supplier will send an updated quote.");
      await loadQuote();
    } catch (e) {
      alert(e.response?.data?.message || "Could not request a revision.");
    } finally {
      setActing(false);
    }
  };

  if (loadError) {
    return <UserLayout><div className="min-h-[60vh] grid place-items-center text-slate-500">{loadError} <Link to="/quotes" className="text-blue-600 ml-1">Back to My Quotes</Link></div></UserLayout>;
  }
  if (!req) {
    return <UserLayout><div className="min-h-[60vh] grid place-items-center text-slate-400">Loading…</div></UserLayout>;
  }

  const state = customerQuoteState(req);
  const pill = PILL[state] || PILL.awaiting;
  const part = quote?.parts?.[0] || {};
  const heroImg = part.image || req.partPhotos?.[0] || req.vehicleImage;
  const pricing = quote?.pricing;
  const attachments = quote?.attachments || [];
  // Request photos are stored separately from supplier quotation attachments.
  const requestPhotos = [
    ...(req.vehicleImage ? [{ url: req.vehicleImage, name: "VIN / Rego photo", type: "image" }] : []),
    ...(req.additionalVehicleImages || []).filter(Boolean).map((url, i) => ({ url, name: `Additional vehicle photo ${i + 1}`, type: "image" })),
    ...(req.partPhotos || []).filter(Boolean).map((url, i) => ({ url, name: `Part photo ${i + 1}`, type: "image" })),
  ];
  const allAttachments = [...requestPhotos, ...attachments];
  const daysLeft = state === "sent" ? quoteExpiry(req.quotedAt) : null;
  const expired = daysLeft != null && daysLeft <= 0;
  const canRespond = state === "sent" && quote?.status === "sent" && quote?.isLatest;
  const olderCount = Math.max(0, comments.length - 4);
  const visibleComments = showOlder ? comments : comments.slice(-4);
  const title = part.name || req.partRequired || "Part Request";
  const subtitle = part.partNumber || req.partNumber || req.notes || "";
  const headerId = req.requestId || "Request";
  const quoteDate = quote ? req.quotedAt : req.createdAt;

  // Item Details rows (design: Make / Model / Year / Engine / Part Number / Supplier / Warranty).
  // Rows with no data are skipped, except Supplier and Warranty.
  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "");
  const itemRows = buildRows([
    ["Make", pick(part.make, req.make, req.vehicleMake)],
    ["Model", pick(part.model, req.model, req.vehicleModel)],
    ["Year", pick(part.year, req.year, req.vehicleYear)],
    ["Engine", pick(part.engine, req.engine, req.vehicleEngine)],
    ["Vehicle", part.vehicle],
    ["VIN / Rego", pick(req.vin, req.rego)],
    ["Condition", part.condition],
    ["Part Number", pick(part.partNumber, req.partNumber)],
    ["Stock", part.stock],
    ["Location", part.location],
    ["KM", part.km],
    ["Delivery", quote?.deliveryType],
    ["Supplier", "All Good Parts", true],
    ["Warranty", quote?.warranty, true],
  ]);

  return (
    <UserLayout>
      <style>{`
/* File-type aware previews used in quote attachments, comments and pending uploads. */
.agp-attachment-gallery { display:grid;grid-template-columns:repeat(auto-fill,minmax(min(100%,170px),1fr));gap:12px;width:100%;margin-top:10px }
.agp-attachment-tile {position:relative;min-width:0;overflow:hidden;border:1px solid #e2e8f0;border-radius:12px;background:#fff;box-shadow:0 2px 7px #0f172a09}
.agp-attachment-visual {position:relative;display:block;width:100%;height:125px;overflow:hidden;background:#f1f5f9;border:0;cursor:zoom-in;padding:0}
.agp-attachment-visual img {width:100%;height:100%;object-fit:cover}
.agp-attachment-visual iframe {display:block;width:100%;height:160px;pointer-events:none;border:0;background:white}
.agp-pdf-mark {position:absolute;left:8px;top:8px;border-radius:5px;padding:3px 6px;background:#dc2626;color:white;font-size:10px;font-weight:800}
.agp-attachment-zoom {position:absolute;right:7px;bottom:7px;display:flex;align-items:center;gap:4px;border-radius:6px;background:#0f172acc;color:#fff;padding:5px 8px;font-size:11px;font-weight:600}
.agp-attachment-media {min-height:125px;display:flex;flex-direction:column;justify-content:center;align-items:center;gap:14px;padding:12px;background:#f5f8ff;color:#2557b7}
.agp-attachment-media audio {width:100%;max-width:100%;height:38px}
.agp-attachment-video {display:block;width:100%;height:125px;object-fit:contain;background:#0f172a}
.agp-attachment-fallback {height:125px;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:8px;color:#64748b;background:#f8fafc;font-size:11px;font-weight:700}
.agp-attachment-footer {display:flex;align-items:center;gap:8px;padding:10px;font-size:12px;color:#273449}
.agp-attachment-footer span {flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.agp-attachment-footer a {flex-shrink:0;color:#1d4ed8}
.agp-attachment-remove {position:absolute;z-index:3;top:5px;right:5px;width:26px;height:26px;display:grid;place-items:center;border-radius:50%;background:#fff;color:#dc2626;box-shadow:0 1px 5px #0003}
.agp-attachment-lightbox {position:fixed;inset:0;z-index:99999;background:#090e19ed;display:flex;align-items:center;justify-content:center;padding:62px 16px 20px}
.agp-attachment-lightbox>img {max-width:100%;max-height:100%;object-fit:contain}
.agp-attachment-lightbox>iframe {width:min(100%,960px);height:100%;border:0;border-radius:8px;background:white}
.agp-attachment-lightbox-header {position:absolute;top:0;left:0;right:0;height:55px;display:flex;align-items:center;gap:15px;padding:0 18px;color:white}
.agp-attachment-lightbox-header span {flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:13px}
.agp-attachment-lightbox-header a,.agp-attachment-lightbox-header button {display:grid;place-items:center;color:white;background:transparent;border:0;cursor:pointer}
.agp-qdm__attachment-inner { padding:14px 16px 18px; min-width:0; }
.agp-qdm__attachment-inner > .text-sm {display:block;line-height:1.5;}
.agp-qdm__chat .agp-qdm__comment-body {padding:0 !important;display:flex;flex-direction:column;min-height:0;height:min(62dvh,490px);overflow:hidden;}
.agp-qdm__chat-messages {min-height:0;flex:1;overflow-y:auto;overscroll-behavior:contain;padding:14px 14px 8px;scrollbar-width:thin;}
.agp-qdm__chat-composer {flex-shrink:0;border-top:1px solid #e5eaf3;background:#fff;padding:10px 12px 12px;}
.agp-qdm__chat-composer .agp-qdm__send {margin:0;}
.agp-qdm__chat-composer .agp-attachment-gallery {max-height:150px;overflow-y:auto;margin:0 0 10px;}
.agp-quote-desktop-composer .agp-attachment-gallery {max-height:135px;overflow-y:auto;}
.agp-quote-desktop-composer > .mt-5 {margin-top:0;}
.agp-quote-desktop-composer > .mt-3 {padding-left:0;}
.agp-quote-desktop-chat {overflow:hidden;}
@media(max-width:600px){.agp-attachment-gallery{grid-template-columns:repeat(2,minmax(0,1fr));gap:9px}.agp-attachment-visual,.agp-attachment-video,.agp-attachment-fallback{height:110px}.agp-attachment-media{min-height:110px;padding:7px}.agp-attachment-footer{padding:8px;font-size:11px}}
`}</style>
      <div className="agp-qdm">
        <div className="agp-qdm__top">
          <button className="agp-qdm__back" onClick={() => navigate("/quotes")} aria-label="Back to quotes"><ChevronLeft size={20} /></button>
          <strong>{headerId}</strong>
        </div>
        <div className="agp-qdm__body">
          {notice && <div className="agp-qdm__notice" role="status">{notice}</div>}
          <StateBanner state={state} quotedAt={req.quotedAt} expired={expired} />
          <section className="agp-qdm__box agp-qdm__summary">
            <div className="agp-qdm__primary">
              <div className="agp-qdm__photo"><SafeImage src={heroImg ? assetUrl(heroImg) : ""} alt={title} /></div>
              <div className="agp-qdm__heading">
                <div className="agp-qdm__pill">{pill.label}<span className="agp-qdm__dot" /></div>
                <div className="agp-qdm__id">{headerId}</div>
                
                <div className="agp-qdm__name">{title}</div>
                <div className="agp-qdm__subtitle">{subtitle}</div>
              </div>
            </div>
            <div className="agp-qdm__stats">
              <div className="agp-qdm__stat"><div className="agp-qdm__statlabel"><Calendar size={12}/> Quote Date</div><div className="agp-qdm__statvalue">{fmtDate(quoteDate)}</div><div className="agp-qdm__subvalue">{fmtClock(quoteDate)}</div></div>
              <div className="agp-qdm__stat"><div className="agp-qdm__statlabel"><Clock size={12}/> Response Time</div><div className="agp-qdm__statvalue">{quote ? duration(req.createdAt, req.quotedAt) : "—"}</div></div>
              <div className="agp-qdm__stat"><div className="agp-qdm__statlabel"><Info size={12}/> Quote Price</div><div className="agp-qdm__statvalue agp-qdm__price">{pricing ? `${money(pricing.total)} AUD` : "Awaiting Quote"}</div></div>
            </div>
          </section>
          <section className="agp-qdm__box">
            <h2 className="agp-qdm__section-title">Item Details</h2>
            <div className="agp-qdm__rows">{itemRows.map((row, i) => {
              const label = Array.isArray(row) ? row[0] : row?.label ?? row?.name;
              const value = Array.isArray(row) ? row[1] : row?.value;
              return <div key={i} className="agp-qdm__row"><span>{label}</span><span>{String(value ?? "—")}</span></div>;
            })}</div>
          </section>
          <section className="agp-qdm__box"><h2 className="agp-qdm__section-title">Quote Description</h2><div className="agp-qdm__copy">{quote ? (quote.customerNote || "No additional notes from the supplier.") : (req.notes || "Your quote description will appear here once the supplier has replied.")}</div></section>
          <section className="agp-qdm__box agp-qdm__attachments"><h2 className="agp-qdm__section-title">Attachments ({allAttachments.length})</h2><div className="agp-qdm__attachment-inner">{requestPhotos.length > 0 && <><h3 className="text-sm font-bold text-slate-800">Uploaded request photos ({requestPhotos.length})</h3><AttachmentGallery items={requestPhotos} onPreview={setPreview} /></>}{attachments.length > 0 && <div className={requestPhotos.length ? "mt-5" : ""}><h3 className="text-sm font-bold text-slate-800">Supplier attachments ({attachments.length})</h3><AttachmentGallery items={attachments} onPreview={setPreview} /></div>}{allAttachments.length === 0 && <span className="text-sm text-slate-400">No attachments added.</span>}</div></section>
          {versionCount > 1 && quote && <Link to={`/quotes/${id}/history`} className="text-center text-sm font-semibold text-blue-700">View quote version history</Link>}
          {canRespond && reviseOpen && <section className="agp-qdm__box agp-qdm__revision"><strong>Request a revision</strong><textarea rows={3} value={reviseText} onChange={(e) => setReviseText(e.target.value)} placeholder="What would you like changed?" /><button type="button" onClick={requestRevision} disabled={acting}>{acting ? "Sending…" : "Send revision request"}</button><button type="button" onClick={() => setReviseOpen(false)} style={{background:"transparent",color:"#475569",marginLeft:10}}>Cancel</button></section>}
          <section className="agp-qdm__box agp-qdm__chat"><h2 className="agp-qdm__section-title">Comments ({comments.length})</h2><div className="agp-qdm__comment-body"><div className="agp-qdm__chat-messages" ref={mobileChatRef}>
            {comments.length === 0 && <p className="text-sm text-slate-400 mb-3">No comments yet.</p>}
            {comments.map((c, i) => <article key={c._id || i} className="agp-qdm__comment"><div className="agp-qdm__comment-head"><span className="agp-qdm__avatar">{initials(c.authorName)}</span><div><div>{String(c.authorId) === String(user?.id) ? "You" : c.authorName}</div><div className="text-[10px] text-slate-400">{fmtDateTime(c.createdAt)}</div></div></div>{c.text && <p>{c.text}</p>}{c.attachments?.length > 0 && <AttachmentGallery items={c.attachments} onPreview={setPreview} />}</article>)}
            </div><div className="agp-qdm__chat-composer"><PendingAttachmentGallery files={files} onRemove={(i) => setFiles(fs => fs.filter((_, ix) => ix !== i))} onPreview={setPreview} /><div className="agp-qdm__send"><button type="button" onClick={() => fileRef.current?.click()} aria-label="Attach"><Paperclip size={19}/></button><input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your message..." onKeyDown={(e) => e.key === "Enter" && !sending && send()}/><button type="button" disabled={sending || (!text.trim() && files.length === 0)} onClick={send} aria-label="Send"><Send size={20}/></button></div></div>
          </div></section>
        </div>
        {canRespond && <div className="agp-qdm__actions"><button className="agp-qdm__approve" onClick={() => respond("approved")} disabled={acting || expired}><Check size={17}/> Approve</button><button className="agp-qdm__decline" onClick={() => respond("declined")} disabled={acting}><X size={17}/> Decline</button><button className="agp-qdm__revise" onClick={() => setReviseOpen(true)} disabled={acting}><Pencil size={16}/> Revise</button></div>}
      </div>
      <div className="agp-quote-detail-desktop relative min-h-[calc(100vh-92px)] bg-[#f7f7f9] pb-12">
        {/* black band behind the top of the summary card */}
        <div className="absolute inset-x-0 top-0 h-[258px] bg-black" />

        <div className="agp-page-wrap relative pt-[34px]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => navigate("/quotes")} aria-label="Back to My Quotes"
                className="grid h-10 w-10 place-items-center rounded-full bg-white text-[#111] shadow-sm hover:bg-slate-100">
                <ChevronLeft size={20} />
              </button>
              <span className="text-[15px] font-bold text-white">{headerId}</span>
            </div>
            {versionCount > 1 && quote && (
              <Link to={`/quotes/${id}/history`} className="inline-flex rounded-lg border border-white/30 px-4 py-2 text-[13px] font-semibold text-white hover:bg-white/10">
                See what changed from Quote {quote._v - 1}
              </Link>
            )}
          </div>

          {notice && <div role="status" className="mt-4 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-800">{notice}</div>}

          <div className="mt-4">
            <StateBanner state={state} quotedAt={req.quotedAt} expired={expired} />
          </div>

          {/* ---------- summary card ---------- */}
          <section className="mt-1 rounded-[12px] bg-white p-[26px] shadow-[0_10px_40px_rgba(6,13,25,.10)]">
            <div className="flex flex-col gap-7 lg:flex-row lg:items-start">
              <div className="grid h-[186px] w-full shrink-0 place-items-center overflow-hidden rounded-[18px] bg-[#f4f4fa] sm:w-[183px]">
                <SafeImage src={heroImg ? assetUrl(heroImg) : ""} alt={title} className="h-full w-full object-contain p-3" />
              </div>

              <div className="min-w-0 flex-1">
                <span className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-[13px] font-bold uppercase ${pill.cls}`}>
                  {pill.label} <i className={`h-2 w-2 rounded-full ${pill.dot}`} />
                </span>
                <h1 className="mt-3 text-[26px] font-extrabold leading-tight text-[#0e1a30]">
                  {title}
                  {quote?.parts?.length > 1 && <span className="text-base font-semibold text-slate-400"> + {quote.parts.length - 1} more</span>}
                </h1>
                <p className="mt-1 text-[14px] text-[#858a94]">{subtitle || " "}</p>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="flex gap-3 rounded-[10px] bg-[#f5f8fd] px-6 py-4">
                    <Calendar size={20} className="mt-1 shrink-0 text-[#176eff]" />
                    <div>
                      <span className="block text-[10px] uppercase text-[#7a808b]">{quote ? "Quote Date" : "Requested"}</span>
                      <strong className="mt-1 block text-[17px] text-[#172238]">{fmtDate(quoteDate)}</strong>
                      <span className="block text-[11px] text-[#9aa0aa]">{fmtClock(quoteDate)}</span>
                    </div>
                  </div>
                  <div className="flex gap-3 rounded-[10px] bg-[#f5f8fd] px-6 py-4">
                    <Clock size={20} className="mt-1 shrink-0 text-[#176eff]" />
                    <div>
                      <span className="block text-[10px] uppercase text-[#7a808b]">Response Time</span>
                      <strong className="mt-1 block text-[17px] text-[#172238]">{quote ? duration(req.createdAt, req.quotedAt) : duration(req.createdAt, new Date())}</strong>
                    </div>
                  </div>
                  <div className="flex gap-3 rounded-[10px] bg-[#f5f8fd] px-6 py-4">
                    <Clock size={20} className="mt-1 shrink-0 text-[#176eff]" />
                    <div>
                      <span className="block text-[10px] uppercase text-[#7a808b]">Supplier</span>
                      <strong className="mt-1 block text-[17px] text-[#172238]">All Good Parts</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="shrink-0 lg:w-[240px] lg:pt-[142px] lg:text-center">
                <span className="block text-[14px] text-[#5d6472]">Quote Price</span>
                <strong className="mt-2 block text-[28px] font-extrabold text-[#06953a]">{pricing ? `${money(pricing.total)} AUD` : "—"}</strong>
                {daysLeft != null && (
                  <span className={`mt-1 block text-xs ${expired ? "text-orange-600" : "text-slate-400"}`}>
                    {expired ? "Expired" : `Valid ${daysLeft} more day${daysLeft === 1 ? "" : "s"}`}
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* ---------- tabs + comments ---------- */}
          <div className="mt-[60px] grid grid-cols-1 items-start gap-[20px] lg:grid-cols-2">
            {/* LEFT */}
            <div className="flex flex-col">
              <TabBar tab={tab} onChange={setTab} />

              <div className="mt-[30px] min-h-[345px]">
                {tab === "attachments" ? <div className="rounded-[14px] bg-white p-6"><h3 className="text-lg font-bold text-[#0e1a30]">Attachments ({allAttachments.length})</h3>{requestPhotos.length > 0 && <div className="mt-4"><h4 className="text-sm font-semibold text-slate-700">Uploaded request photos ({requestPhotos.length})</h4><AttachmentGallery items={requestPhotos} onPreview={setPreview} /></div>}{attachments.length > 0 && <div className="mt-4"><h4 className="text-sm font-semibold text-slate-700">Supplier attachments ({attachments.length})</h4><AttachmentGallery items={attachments} onPreview={setPreview} /></div>}{allAttachments.length === 0 && <p className="mt-4 text-sm text-slate-400">No attachments added.</p>}</div> : <TabPanel
                  tab={tab}
                  rows={itemRows}
                  pricing={quote ? pricing : null}
                  description={quote ? (quote.customerNote || "No additional notes from the supplier.") : (req.notes || "Your quote description will appear here once the supplier has replied.")}
                  attachments={attachments}
                />}
              </div>

              {/* revise box + actions */}
              {canRespond && reviseOpen && (
                <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50/40 p-4">
                  <label className="text-sm font-semibold text-slate-700" htmlFor="revise-text">What would you like changed?</label>
                  <textarea id="revise-text" value={reviseText} onChange={(e) => setReviseText(e.target.value)} rows={3}
                    placeholder="e.g. Lower km option, or include freight to Geelong"
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <div className="mt-2 flex justify-end gap-2">
                    <button onClick={() => { setReviseOpen(false); setReviseText(""); }} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
                    <button onClick={requestRevision} disabled={acting}
                      className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                      {acting ? "Sending…" : "Send revision request"}
                    </button>
                  </div>
                </div>
              )}

              {canRespond && (
                <div className="mt-[50px] grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <button onClick={() => respond("approved")} disabled={acting || expired}
                    title={expired ? "This quote has expired. Ask for an updated quote." : ""}
                    className="flex h-[53px] items-center justify-center gap-2 rounded-[6px] bg-[#0b7be7] text-[15px] font-medium text-white hover:bg-[#086fd1] disabled:opacity-60">
                    <Check size={16} /> Approve
                  </button>
                  <button onClick={() => respond("declined")} disabled={acting}
                    className="flex h-[53px] items-center justify-center gap-2 rounded-[6px] bg-[#e32832] text-[15px] font-medium text-white hover:bg-[#c91f29] disabled:opacity-60">
                    <X size={16} /> Decline
                  </button>
                  <button onClick={() => setReviseOpen(true)} disabled={acting}
                    className="flex h-[53px] items-center justify-center gap-2 rounded-[6px] border border-[#0b7be7] bg-white text-[15px] font-medium text-[#0b7be7] hover:bg-blue-50 disabled:opacity-60">
                    <Pencil size={15} /> {expired ? "Request Update" : "Revise"}
                  </button>
                </div>
              )}
            </div>

            {/* RIGHT — comments */}
            <section className="agp-quote-desktop-chat flex min-h-0 flex-col rounded-[12px] bg-white p-[22px] lg:h-[610px]">
              <h3 className="text-[17px] font-bold text-[#0e1a30]">Comments ({comments.length})</h3>

              <div ref={desktopChatRef} className="mt-5 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-2">
                {olderCount > 0 && !showOlder && (
                  <button onClick={() => setShowOlder(true)} className="mb-4 flex w-full items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-700">
                    Show {olderCount} older comment{olderCount === 1 ? "" : "s"} <ChevronDown size={14} />
                  </button>
                )}
                {visibleComments.map((c, idx) => {
                  const mine = String(c.authorId) === String(user?.id);
                  const last = idx === visibleComments.length - 1;
                  return (
                    <div key={c._id} className="relative flex gap-4 pb-6">
                      {!last && <span className="absolute left-[27px] top-[56px] bottom-0 border-l border-dashed border-[#d7dae2]" />}
                      <span className={`z-[1] grid h-[56px] w-[56px] shrink-0 place-items-center rounded-full text-[22px] font-extrabold ${mine ? "bg-[#e4e5ea] text-[#111]" : "bg-[#e3ebff] text-[#2f6df6]"}`}>
                        {initials(c.authorName)}
                      </span>
                      <div className="min-w-0 flex-1 pt-1">
                        <div className="flex flex-wrap items-baseline gap-x-3">
                          <span className="text-[15px] font-bold text-[#0e1a30]">{mine ? "You" : c.authorName}</span>
                          {!mine && <span className="text-[12px] font-semibold text-[#2f6df6]">Supplier</span>}
                          <span className="text-[11px] text-[#7c828d]">{fmtDateTime(c.createdAt)}</span>
                        </div>
                        {c.text && <p className="mt-2 whitespace-pre-line text-[14.5px] text-[#555b66]">{c.text}</p>}
                        {c.attachments?.length > 0 && (
                          <AttachmentGallery items={c.attachments} onPreview={setPreview} />
                        )}
                      </div>
                    </div>
                  );
                })}
                {comments.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No comments yet. Ask us anything about this part.</p>}
              </div>
              <div className="agp-quote-desktop-composer mt-3 border-t border-slate-100 pt-3">
              <div className="mt-5 flex items-center gap-4">
                <span className="grid h-[56px] w-[56px] shrink-0 place-items-center rounded-full bg-[#e4e5ea] text-[22px] font-extrabold text-[#111]">{initials(user?.name)}</span>
                <div className="flex h-[57px] flex-1 items-center gap-2 rounded-[10px] border border-[#e8eaf0] px-5">
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && !sending && send()}
                    placeholder="Add a comment"
                    className="min-w-0 flex-1 text-[14px] outline-none placeholder:text-[#555]"
                  />
                  <button type="button" onClick={() => fileRef.current?.click()} aria-label="Attach files" className="text-[#2f6df6]"><Paperclip size={20} /></button>
                  <input ref={fileRef} type="file" multiple hidden
                    accept="image/*,application/pdf,audio/*,video/*"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                </div>
                <button type="button" onClick={send} disabled={sending} aria-label="Send comment" className="grid h-10 w-10 place-items-center text-[#2f6df6] disabled:opacity-50">
                  <Send size={22} />
                </button>
              </div>

              {files.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 pl-[72px]">
                  <PendingAttachmentGallery files={files} onRemove={(i) => setFiles((prev) => prev.filter((_, idx) => idx !== i))} onPreview={setPreview} />
                </div>
              )}

              </div>
            </section>
          </div>
        </div>
      </div>
      {preview && <AttachmentLightbox item={preview} onClose={() => setPreview(null)} />}
    </UserLayout>
  );
}

function StateBanner({ state, quotedAt, expired }) {
  const map = {
    awaiting: ["bg-amber-50 text-amber-800", "We're finding your part. Your quote will appear here as soon as it's ready."],
    updating: ["bg-slate-100 text-slate-700", `The supplier is updating this quote. You're seeing the version sent on ${fmtDate(quotedAt)}.`],
    revision_requested: ["bg-purple-50 text-purple-800", "You asked for a revision. We'll send an updated quote soon."],
    approved: ["bg-green-50 text-green-800", "You approved this quote. We'll contact you to arrange delivery."],
    declined: ["bg-red-50 text-red-700", "You declined this quote. Send us a comment if you'd like another option."],
    sent: expired
      ? ["bg-orange-50 text-orange-800", "This quote has expired. Request an updated quote to get current pricing."]
      : null,
  };
  const entry = map[state];
  if (!entry) return null;
  return (
    <div className={`mb-4 flex items-start gap-2 rounded-xl px-4 py-3 text-sm shadow-sm ${entry[0]}`}>
      <Info size={16} className="shrink-0 mt-0.5" /> <span>{entry[1]}</span>
    </div>
  );
}


// Supports server attachments (URL/string) and files selected locally.
function attachmentInfo(item, index = 0) {
  const raw = typeof item === "string" ? { url: item } : (item || {});
  const url = raw.localUrl || (raw.url ? assetUrl(raw.url) : "");
  const name = raw.name || (typeof raw.url === "string" ? decodeURIComponent(raw.url.split("?")[0].split("/").pop() || "") : "") || `Attachment ${index + 1}`;
  const mime = String(raw.mimeType || raw.mimetype || raw.contentType || raw.type || "").toLowerCase();
  const cleanPath = `${name} ${typeof raw.url === "string" ? raw.url.split("?")[0] : ""}`.toLowerCase();
  const ext = (pattern) => pattern.test(cleanPath);
  const kind = mime.startsWith("image/") || mime === "image" || ext(/\.(png|jpe?g|webp|gif|bmp|svg|avif|heic)(?:\s|$)/) ? "image"
    : mime.includes("pdf") || ext(/\.pdf(?:\s|$)/) ? "pdf"
    : mime.startsWith("audio/") || mime === "audio" || ext(/\.(mp3|wav|ogg|m4a|aac|flac|opus)(?:\s|$)/) ? "audio"
    : mime.startsWith("video/") || mime === "video" || ext(/\.(mp4|webm|mov|m4v|ogv)(?:\s|$)/) ? "video" : "file";
  return { url, name, kind };
}

function AttachmentGallery({ items = [], onPreview }) {
  if (!items.length) return <span className="text-sm text-slate-400">No attachments added.</span>;
  return <div className="agp-attachment-gallery">
    {items.map((raw, index) => <AttachmentTile key={`${typeof raw === "string" ? raw : raw?.url || raw?.name}-${index}`} item={attachmentInfo(raw, index)} onPreview={onPreview} />)}
  </div>;
}

function AttachmentTile({ item, onPreview, onRemove }) {
  const { url, name, kind } = item;
  const Icon = kind === "pdf" ? FileText : kind === "audio" ? FileAudio : kind === "video" ? FileVideo : kind === "image" ? ImageIcon : GenericFile;
  return <div className="agp-attachment-tile">
    {onRemove && <button type="button" className="agp-attachment-remove" onClick={onRemove} aria-label={`Remove ${name}`}><X size={15}/></button>}
    {(kind === "image" || kind === "pdf") ? (
      <button type="button" className="agp-attachment-visual" onClick={() => onPreview(item)} aria-label={`Preview ${name}`}>
        {kind === "image" ? <img src={url} alt={name} loading="lazy" /> : <><iframe src={`${url}#toolbar=0&navpanes=0&scrollbar=0`} title={`${name} thumbnail`} tabIndex={-1} loading="lazy" /><span className="agp-pdf-mark">PDF</span></>}
        <span className="agp-attachment-zoom"><Maximize2 size={15}/> Preview</span>
      </button>
    ) : kind === "audio" ? <div className="agp-attachment-media"><FileAudio size={23}/><audio controls preload="metadata" src={url} /></div>
      : kind === "video" ? <video controls preload="metadata" src={url} className="agp-attachment-video" />
      : <div className="agp-attachment-fallback"><Icon size={34}/><span>FILE</span></div>}
    <div className="agp-attachment-footer"><span title={name}>{name}</span>{url && <a href={url} target="_blank" rel="noreferrer" download={name} title={`Open or download ${name}`} onClick={(e) => e.stopPropagation()}><Download size={16}/></a>}</div>
  </div>;
}

function PendingAttachmentGallery({ files, onRemove, onPreview }) {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    const next = files.map((file) => ({ file, url: URL.createObjectURL(file) }));
    setEntries(next);
    return () => next.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [files]);
  if (!files.length) return null;
  return <div className="agp-attachment-gallery">{entries.map(({ file, url }, i) => <AttachmentTile key={`${file.name}-${i}`} item={attachmentInfo({ name: file.name, type: file.type, localUrl: url }, i)} onPreview={onPreview} onRemove={() => onRemove(i)} />)}</div>;
}

function AttachmentLightbox({ item, onClose }) {
  useEffect(() => {
    const key = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", key);
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", key); document.body.style.overflow = original; };
  }, [onClose]);
  return <div className="agp-attachment-lightbox" role="dialog" aria-modal="true" aria-label={`Preview ${item.name}`} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="agp-attachment-lightbox-header"><span>{item.name}</span><a href={item.url} download={item.name} target="_blank" rel="noreferrer" aria-label="Download attachment"><Download size={19}/></a><button type="button" onClick={onClose} aria-label="Close preview"><X size={22}/></button></div>
    {item.kind === "image" ? <img src={item.url} alt={item.name} /> : <iframe src={item.url} title={item.name} />}
  </div>;
}
